import { getEntitiesByDatabaseId } from 'api/bitedb/entities/entities.service';

export const MAX_SELECTED_ENTITIES = 5000;

/** Rows per page until the picker has measured the room it has. */
export const PICKER_PAGE_SIZE = 10;

export interface DetailsOnFile {
  website: boolean;
  email: boolean;
  phone: boolean;
  address: boolean;
}

/** A Database Entity the picker can add to a Bulk Verification Run. */
export interface BulkVerificationCandidate {
  databaseEntityId: string;
  name: string;
  country?: string;
  details: DetailsOnFile;
  /** No details any Verification Check needs; the run would skip it. */
  nothingToVerify: boolean;
}

export interface BulkVerificationCandidatePage {
  candidates: BulkVerificationCandidate[];
  totalPages: number;
}

const present = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const isCanadian = (country?: string): boolean =>
  ['canada', 'ca', 'can'].includes((country || '').trim().toLowerCase());

const databaseEntityIdOf = (row: any): string | undefined => {
  const id = row?.association_id ?? row?.database_entity_id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
};

const candidateOf = (row: any): BulkVerificationCandidate | null => {
  const databaseEntityId = databaseEntityIdOf(row);
  if (!databaseEntityId) return null;
  const details: DetailsOnFile = {
    website: present(row.domain),
    email: present(row.email),
    phone: present(row.phone),
    address: present(row.address),
  };
  const country = present(row.country_of_origin)
    ? String(row.country_of_origin)
    : undefined;
  // Canadian records can still be checked against the registries by name.
  const hasAnyDetail =
    details.website || details.email || details.phone || details.address;
  return {
    databaseEntityId: databaseEntityId,
    name: present(row.name) ? String(row.name) : 'Unnamed entity',
    country: country,
    details: details,
    nothingToVerify: !hasAnyDetail && !isCanadian(country),
  };
};

/** One page of the database's entities, by name, for the wizard's picker. */
export const loadBulkVerificationCandidates = async (
  databaseId: string,
  {
    search,
    page,
    pageSize = PICKER_PAGE_SIZE,
  }: { search: string; page: number; pageSize?: number },
): Promise<BulkVerificationCandidatePage> => {
  const response = await getEntitiesByDatabaseId(databaseId, {
    page: page,
    limit: pageSize,
    sortBy: 'name',
    sortOrder: 'asc',
    search: search.trim(),
    tab: 'All',
  });
  return {
    candidates: (response?.data || [])
      .map(candidateOf)
      .filter((c): c is BulkVerificationCandidate => c !== null),
    totalPages: Math.max(1, Number(response?.pagination?.totalPages) || 1),
  };
};
